import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiPackage, FiCheck, FiShoppingCart, FiGift } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const CreatorBundles = ({ profile, theme }) => {
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  const bundles = [
    {
      id: 'starter',
      name: 'Creator Starter',
      description: 'Perfect for emerging creators',
      price: '0.15 ETH',
      originalPrice: '0.25 ETH',
      savings: '40%',
      features: [
        'All 3 basic themes',
        '1 premium theme',
        'Tip jar integration',
        'Basic analytics',
        'Email support',
        'Community access'
      ],
      badge: 'Popular',
      badgeColor: 'bg-green-500'
    },
    {
      id: 'pro',
      name: 'Creator Pro',
      description: 'For serious content creators',
      price: '0.35 ETH',
      originalPrice: '0.60 ETH',
      savings: '42%',
      features: [
        'All premium themes',
        'NFT gallery',
        'Token-gated content',
        'Advanced analytics',
        'Priority support',
        'Creator community',
        'Early access to features',
        'Profile verification badge'
      ],
      badge: 'Best Value',
      badgeColor: 'bg-purple-500'
    },
    {
      id: 'enterprise',
      name: 'Creator Enterprise',
      description: 'For established creators and brands',
      price: '0.75 ETH',
      originalPrice: '1.20 ETH',
      savings: '38%',
      features: [
        'Everything in Pro',
        'Custom theme development',
        'White-label options',
        'API access',
        'Dedicated account manager',
        'Advanced customization',
        'Team collaboration',
        'Advanced analytics & insights',
        'Priority feature requests'
      ],
      badge: 'Premium',
      badgeColor: 'bg-yellow-500'
    }
  ];

  const handlePurchase = (bundle) => {
    setSelectedBundle(bundle);
    setShowPurchaseModal(true);
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-white mb-2">Creator Bundles</h3>
        <p className="text-gray-400 max-w-2xl mx-auto">
          Get everything you need to build your Web3 presence with our curated bundles
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {bundles.map((bundle, index) => (
          <motion.div
            key={bundle.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className={`relative rounded-xl border-2 ${bundle.id === 'pro' ? 'border-lime-500 shadow-xl scale-105 shadow-glow-lime' : 'border-dark-700'} glass-dark overflow-hidden hover:border-lime-500/50 transition-all`}
          >
            {/* Badge */}
            <div className={`absolute top-0 right-0 ${bundle.id === 'pro' ? 'bg-lime-500' : 'bg-dark-700'} text-white text-xs font-bold px-3 py-1 rounded-bl-lg`}>
              {bundle.badge}
            </div>
            
            {/* Bundle Header */}
            <div className={`p-6 ${bundle.id === 'pro' ? 'bg-gradient-to-br from-lime-500/10 to-green-500/10 border-b border-lime-500/30' : 'border-b border-dark-700'}`}>
              <div className="flex items-center justify-center mb-4">
                <div className={`w-16 h-16 ${bundle.id === 'pro' ? 'bg-lime-500' : 'bg-dark-700'} rounded-xl flex items-center justify-center border ${bundle.id === 'pro' ? 'border-lime-500/50' : 'border-dark-600'}`}>
                  <SafeIcon icon={FiPackage} className="text-white text-2xl" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-white text-center mb-2">
                {bundle.name}
              </h3>
              <p className="text-dark-300 text-center text-sm mb-4">
                {bundle.description}
              </p>
              {/* Pricing */}
              <div className="text-center">
                <div className="flex items-center justify-center space-x-2 mb-1">
                  <span className="text-2xl font-bold text-lime-400">{bundle.price}</span>
                  {bundle.originalPrice && (
                    <span className="text-lg text-dark-500 line-through">{bundle.originalPrice}</span>
                  )}
                </div>
                {bundle.savings && (
                  <span className="text-lime-400 text-sm font-medium">
                    Save {bundle.savings}
                  </span>
                )}
              </div>
            </div>

            {/* Features */}
            <div className="p-6">
              <h4 className="font-semibold text-white mb-4">What's Included:</h4>
              <div className="space-y-3 mb-6">
                {bundle.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-start">
                    <SafeIcon icon={FiCheck} className="text-lime-400 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-dark-200 text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Purchase Button */}
              <button
                onClick={() => handlePurchase(bundle)}
                className={`w-full py-3 rounded-lg font-medium transition-all transform hover:scale-105 ${
                  bundle.id === 'pro' 
                    ? 'glass-lime text-lime-400 border border-lime-500/50 hover:text-lime-300 shadow-glow-lime-sm' 
                    : 'glass text-white border border-dark-700 hover:text-lime-400 hover:border-lime-500/50'
                }`}
              >
                <SafeIcon icon={FiShoppingCart} className="mr-2" />
                Get Bundle
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Bundle Comparison */}
      <div className="glass-dark rounded-xl border border-dark-700 p-6">
        <h4 className="text-lg font-semibold text-white mb-4">Bundle Comparison</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-700">
                <th className="text-left py-2 text-dark-200">Feature</th>
                <th className="text-center py-2 text-dark-200">Starter</th>
                <th className="text-center py-2 text-dark-200">Pro</th>
                <th className="text-center py-2 text-dark-200">Enterprise</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-dark-800">
                <td className="py-2 text-dark-200">Basic Themes</td>
                <td className="text-center text-lime-400">✓</td>
                <td className="text-center text-lime-400">✓</td>
                <td className="text-center text-lime-400">✓</td>
              </tr>
              <tr className="border-b border-dark-800">
                <td className="py-2 text-dark-200">Premium Themes</td>
                <td className="text-center text-white">1</td>
                <td className="text-center text-lime-400">All</td>
                <td className="text-center text-lime-400">All + Custom</td>
              </tr>
              <tr className="border-b border-dark-800">
                <td className="py-2 text-dark-200">NFT Gallery</td>
                <td className="text-center text-dark-500">-</td>
                <td className="text-center text-lime-400">✓</td>
                <td className="text-center text-lime-400">✓</td>
              </tr>
              <tr className="border-b border-dark-800">
                <td className="py-2 text-dark-200">Token-Gated Content</td>
                <td className="text-center text-dark-500">-</td>
                <td className="text-center text-lime-400">✓</td>
                <td className="text-center text-lime-400">✓</td>
              </tr>
              <tr className="border-b border-dark-800">
                <td className="py-2 text-dark-200">API Access</td>
                <td className="text-center text-dark-500">-</td>
                <td className="text-center text-dark-500">-</td>
                <td className="text-center text-lime-400">✓</td>
              </tr>
              <tr>
                <td className="py-2 text-dark-200">Support</td>
                <td className="text-center text-white">Email</td>
                <td className="text-center text-white">Priority</td>
                <td className="text-center text-white">Dedicated</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Purchase Modal */}
      {showPurchaseModal && selectedBundle && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-dark rounded-xl border border-dark-700 p-6 max-w-md w-full"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-lime-500 to-green-500 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-glow-lime">
                <SafeIcon icon={FiGift} className="text-dark-950 text-2xl"/>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                {selectedBundle.name}
              </h3>
              <p className="text-dark-300 mb-4">
                {selectedBundle.description}
              </p>
              <div className="text-3xl font-bold text-lime-400 mb-2">
                {selectedBundle.price}
              </div>
              {selectedBundle.savings && (
                <div className="text-lime-400 font-medium">
                  You're saving {selectedBundle.savings}!
                </div>
              )}
            </div>
            <div className="space-y-3">
              <button
                onClick={() => {
                  // Handle bundle purchase
                  alert(`Purchasing ${selectedBundle.name} for ${selectedBundle.price}`);
                  setShowPurchaseModal(false);
                }}
                className="w-full px-4 py-3 glass-lime text-lime-400 rounded-lg font-medium hover:text-lime-300 transition-colors border border-lime-500/50 shadow-glow-lime-sm"
              >
                Complete Purchase
              </button>
              <button
                onClick={() => setShowPurchaseModal(false)}
                className="w-full px-4 py-3 glass text-white rounded-lg font-medium hover:text-lime-400 transition-colors border border-dark-700"
              >
                Maybe Later
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default CreatorBundles;